import AgTechTitleDetailHeader from '../../Headers/AgTechTitleDetailHeader';
import React, { useEffect, useRef } from 'react'
import { AgTechButton, AgTechClickEventHandler } from '../../Buttons/AgTechButtons';
import AgTechModal from './AgTechModal';
import { AgTechConfirmationProps, DefaultAgTechConfirmationActions } from '@agtech/core/functions/AgTechAppConfirmations';
import AgTechHeaderDetail from '../../Headers/AgTechHeaderDetail';
import { logDev } from '@agtech/core/logging/AgTechLogger';

export type AgTechConfirmationModalProps = AgTechConfirmationProps & {
    height?: string,
    width?: string,
    closeModal: () => void,
    onCancel: () => void,
    onConfirm: () => Promise<void>
}

let AgTechConfirmationModal = (props: AgTechConfirmationModalProps) => (
    <AgTechModal
        width={props.width ?? ''}
        closeModal={props.closeModal}
        showCloseButton={false}
        styles={{
            maxWidth: 420
        }}
    >
        <AgTechConfirmationModalContent {...props} />
    </AgTechModal>
);

let AgTechConfirmationModalContent = (props: AgTechConfirmationModalProps) => {
    let confirmButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }
    }, []);

    let handleCancel = (e: AgTechClickEventHandler) => {
        e.clickEvent.stopPropagation();
        props.onCancel();
        close();
    }

    let handleConfirm = (e: AgTechClickEventHandler) => {
        e.clickEvent.stopPropagation();
        props.onConfirm();
    }

    return (
        <div className='agtech-confirmation-modal-content'>
            <div className="tall row-based-vertically-centered flexed p-4 pt-3 flex">
                <AgTechHeaderDetail
                    header={props.content.header}
                    detail={props.content.details}
                    settings={{
                        header: {
                            classes: '!text-plus'
                        },
                        detail: {
                            classes: '!mt-[6px]'
                        }
                    }}
                />
            </div>
            <div className="wide column-based-vertically-centered p-3 mt-2 flex">
                <AgTechButton
                    classes="ml-auto mr-2"
                    text={props.content.cancelButtonText ?? "Cancel"}
                    icon="fas fa-times"
                    action={handleCancel}
                />
                <AgTechButton
                    text={props.content.confirmationButtonText ?? "Continue"}
                    icon="fas fa-check"
                    classes='button-highlight'
                    ref={confirmButtonRef}
                    action={handleConfirm}
                />
            </div>
        </div>
    )
}

export const showConfirmationModal = async (
    props: AgTechConfirmationProps,
    context: {
        showModal: (modalRenderProp: (modalId: string) => React.ReactNode) => string,
        closeModal: (modalId: string) => void
    }
) => {
    return new Promise<boolean>(function(resolve) {
        context.showModal((modalId) => {
            const modalActions = props.actions ?? DefaultAgTechConfirmationActions;

            const modalProps: AgTechConfirmationModalProps = {
                ...props,
                width: props.content.width,
                closeModal: () => context.closeModal(modalId),
                onCancel: () => {
                    if (modalActions.onCancel) {
                        modalActions.onCancel();
                    }
                    
                    context.closeModal(modalId);
                    resolve(false);
                },
                onConfirm: async () => {
                    context.closeModal(modalId);
                    let didConfirmationSucceed = modalActions?.onConfirm ? await modalActions.onConfirm() : true;
    
                    resolve(didConfirmationSucceed);
                }
            };

            logDev('Showing confirmation modal', modalProps);

            return (
                <AgTechConfirmationModal {...modalProps} />
            );
        });
    });
}

export default AgTechConfirmationModal;